import * as React from 'react';
import { useIntl } from 'react-intl';

import { Grid, ModalContext } from '@notino/react-styleguide';
import { getImageAttributes } from '@notino/react-web-utils';

import { ContentWrapper } from '@components';
import { DrawerProvider } from '@components/BottomSheet/DrawerProvider';
import { Divider } from '@components/Divider';
import ErrorBoundary from '@components/ErrorBoundary';
import { PageGone } from '@components/NotFound';
import { ProductQueryParams } from '@containers/ProductDetailContainer';
import { useGetProductView } from '@containers/ProductDetailContainer/hooks/useGetProductView';
import { useSettings } from '@containers/ProductDetailContainer/hooks/useSettings';
import { Breadcrumb } from '@containers/ProductDetailContainer/ProductDetail/Breadcrumb';
import { useShouldShowReviewPage } from '@containers/ProductDetailContainer/ProductDetail/hooks/useShouldShowReviewPage';
import { getProductFullName } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/utils';
import {
  ProductDetailContextProvider,
  useProductDetailContext,
} from '@containers/ProductDetailContainer/ProductDetailContext';
import { ProductStateHandler } from '@containers/ProductDetailContainer/ProductStateHandler';
import { dispatchTrackingEvent } from '@context/tracking/utils';

import { DESCRIPTION_MIN_LENGTH } from './constants';
import { Header } from './Header';
import { HeadMetaReviewsPage } from './HeadMeta/HeadMetaReviewsPage';
import { headMetaMessages } from './HeadMeta/messages';
import { Reviews } from './Reviews';
import { ReviewsPageWrapper } from './styled';
import { getProductName } from './utils';

const ReviewsPage = () => {
  const { product, currentVariant, currentCurrency } =
    useProductDetailContext();
  const { Settings: { imageDomainPath } = {} } = useSettings();
  const { formatMessage } = useIntl();

  const shouldShowReviewPage = useShouldShowReviewPage(
    product.reviewOverview?.textReviewsCount,
    product.reviewOverview?.rating
  );

  React.useEffect(() => {
    dispatchTrackingEvent({
      event: 'page_view',
      page: {
        path: window.location.pathname,
        title: formatMessage(headMetaMessages.seoTitle, {
          productName: getProductName(currentVariant, product),
        }),
        type: 'review',
        unified_name: `product-${currentVariant.productCode}`,
        subpage: undefined,
      },
      _clear: true,
    });
  }, [formatMessage, product, currentVariant]);

  const productName = getProductName(currentVariant, product);
  const productFullName = getProductFullName(product);

  const annotation =
    currentVariant.attributes && currentVariant.attributes.Master
      ? product.annotation
      : currentVariant.variantName;

  const imageAttrs = getImageAttributes(
    currentVariant.media.mainImage,
    imageDomainPath,
    'detailMain'
  );

  const breadcrumbs = [
    ...product.breadcrumb,
    {
      name: product.brand.name,
      link: product.brand.url,
    },
    {
      name: getProductName(currentVariant, product),
      link: product.url,
    },
  ];

  const description = product.description.split(/<p>(.*?)<\/p>/g)?.[1] || '';

  const simpleDescription =
    description.length > DESCRIPTION_MIN_LENGTH ? description : null;

  if (!shouldShowReviewPage) {
    return <PageGone />;
  }

  return (
    <ReviewsPageWrapper>
      <ErrorBoundary>
        <HeadMetaReviewsPage
          simpleDescription={simpleDescription}
          product={product}
          variant={currentVariant}
        />
      </ErrorBoundary>

      <ErrorBoundary>
        <ContentWrapper>
          <Grid fluid={true}>
            <Breadcrumb alignToRight={false} items={breadcrumbs} />

            <Header
              simpleDescription={simpleDescription}
              imageAttrs={imageAttrs}
              annotation={annotation}
              productFullName={productFullName}
              productName={productName}
              redirectUrl={product.url}
              selectedVariant={currentVariant}
              currency={currentCurrency}
            />

            <Divider shouldShowOnMobile={true} />

            <Reviews
              productCode={product.masterProductCode}
              masterId={product.webMasterId}
              isAllReviewsPage={true}
              product={product}
              imageDomainPath={imageDomainPath}
              selectedVariant={currentVariant}
            />
          </Grid>
        </ContentWrapper>
      </ErrorBoundary>
    </ReviewsPageWrapper>
  );
};

const WrappedReviewsPage: React.FC<ProductQueryParams> = ({
  catalogId,
  productId,
  ssrEnabled,
}) => {
  const { data, error, loading } = useGetProductView({
    catalogId,
    productId,
    ssrEnabled,
  });

  return (
    <ProductStateHandler
      data={data}
      error={error}
      loading={loading}
      productId={productId}
      isReviewPage={true}
    >
      <ProductDetailContextProvider PDView={data}>
        <ModalContext.Provider>
          <DrawerProvider>
            <ReviewsPage />
          </DrawerProvider>
        </ModalContext.Provider>
      </ProductDetailContextProvider>
    </ProductStateHandler>
  );
};

export default WrappedReviewsPage;
